/**
 * 封装请求，对请求及响应拦截，并增加处理
 *
 * 请求拦截：
 * 1. Access Token (暂时不用)
 *
 * 响应拦截（结果存储到 store - 否则会依赖国际化、消息弹窗组件、路由跳转等）：
 * 1. 网络信息
 *    store.dispatch('app/toggleNetwork', { health: true })
 * 2. API 请求结果
 *    成功
 *    store.dispatch('app/updateApiResponse',
 *                    { http_status: 201, http_verb: 'POST', api: '/activities' })
 *    错误（如：请求失败，操作无法完成）
 *    store.dispatch('app/updateApiResponse',
 *                    { http_status: 400,
 *                      http_verb: 'POST', api: '/activities',
 *                      error_code: 'record_duplicated', error_msg: '记录已经存在' })
 */
import axios from 'axios'
import store from '@/stores'
import RequestProcessor from '@/helpers/request_processor'
import toast from '@/helpers/toast'
import i18n from '@/helpers/i18n'

const requestProcessor = new RequestProcessor()

// 生产环境需要（dev 使用 vue.config.js dev proxy）
if (process.env.NODE_ENV === 'production') {
  axios.defaults.baseURL = process.env.VUE_APP_API_URI
}

// 跨域资源共享 CORS 详解: http://www.ruanyifeng.com/blog/2016/04/cors.html
axios.defaults.withCredentials = true

// http request 拦截器
axios.interceptors.request.use(
  config => {
    // let token = localStorage.getItem('token')
    // if (token) {
    //   config.headers.common['Authorization'] = 'Bearer ' + token
    // }
    if (!config.url.includes('/options/')) {
      // 特殊处理，选项不计入到普通的 loading 中，后期可以增加一个 loading。否则会导致页面经常出现loading闪烁
      requestProcessor.showLoading()
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// http response 拦截器
axios.interceptors.response.use(
  function(response) {
    if (!response.config.url.includes('/options/')) {
      // 特殊处理，选项不计入到普通的 loading 中，后期可以增加一个 loading。否则会导致页面经常出现loading闪烁
      // 隐藏 loading
      requestProcessor.hideLoading()
    }

    // 网络信号健康通知
    store.dispatch('app/toggleNetwork', { health: true })

    // Do something with response data
    return response
  },
  function(error) {
    // 隐藏 loading
    requestProcessor.hideLoading()

    // Do something with response error
    if (error.response) {
      // let content = ''
      let code = error.response.status

      if (code === 400) {
        // 一般的业务错误请求处理，需要 axios 的使用者主动捕获该信息
        // console.error(code, error.response.data)
        // 针对非标准错误(一般是没有使用 return failure(...)，如验证异常)，提示
        if (error.response.data.error) {
          toast.error(error.response.data.error)
        } else if (error.response.data.error_msg) {
          // 针对某些页面遗漏 catch 400 错误的，补充提示
          toast.error(error.response.data.error_msg)
        }
      } else if (code === 401) {
        // 删除是否登录标志
        localStorage.removeItem('is_login')
        // 验证失败，返回到登录页
        window.location.href = '/auth.html#/login'
      } else if (code === 403) {
        // 没有权限访问该接口
        const errormsg = i18n.t('errormsg.msg.no_permission', [requestProcessor.getApiStr(error.config)])
        toast.error(errormsg)
      } else if (code === 422) {
        toast.error(error.response.data.error_msg || i18n.t('errormsg.http_status.422'))
      } else {
        toast.error(i18n.t('errormsg.http_status.500'))
      }
    } else {
      // 网络信号不健康通知
      if (requestProcessor.isNetworkError(error.message)) {
        store.dispatch('app/toggleNetwork', { health: false })
      }
    }

    return Promise.reject(error)
  }
)

export default axios
